import { Box, Text } from "@chakra-ui/react"
import { htmlToText } from "@pathwright/utils/src/text/htmlToText"
import { useEffect, useRef } from "react"
import TruncatedHTML, { TruncatedHtmlProps } from "./TruncatedHTML"

export const HTMLTextStyles = {
  h1: { fontSize: "2xl" },
  h2: { fontSize: "xl" },
  h3: { fontSize: "lg" },
  h4: { fontSize: "md" },
  "h1, h2, h3, h4, h5, h6": { fontWeight: "revert" },
  "h2, h3, h4, h5, h6": { my: "1em" },
  p: { my: [0.5, 0.5, 1, 2] },
  pre: { fontFamily: "mono", bg: "gray.100", p: 2, rounded: "md" },
  code: { fontFamily: "mono", bg: "gray.100" },
  blockquote: {
    borderLeft: "4px solid",
    borderColor: "gray.200",
    pl: 2,
    my: [0.5, 0.5, 1, 2],
    mx: [0, 0, 1, 4]
  },
  ul: { pl: "2em", mb: "15px", listStyleType: "disc" },
  ol: { pl: "2em", mb: "15px", listStyleType: "decimal" },
  "ol ol": { listStyleType: "lower-alpha" },
  "ol ol ol": { listStyleType: "lower-roman" },
  a: {
    textDecoration: "underline",
    color: "brand.500",
    _hover: { color: "brand.500", textDecoration: "underline" }
  },
  "li[class^='pwai-']": {
    listStyleType: "none",
    fontFamily: "var(--chakra-fonts-body) !important",
    fontWeight: 400,
    lineHeight: "1.4em",
    position: "relative"
  },
  "li[class^='pwai-']:before": {
    position: "absolute",
    left: "-26px",
    fontFamily: "pathicons !important"
  },
  img: {
    borderRadius: "10px"
  }
}

type HTMLTextProps = {
  html: string
  asInlineText?: boolean
  truncate?: boolean
  truncateProps?: Omit<TruncatedHtmlProps, "html">
} & React.ComponentProps<typeof Text>

const HTMLText = ({
  html,
  asInlineText = false,
  truncate = false,
  truncateProps,
  ...props
}: HTMLTextProps) => {
  // If true, asInlineText means we should get the textContent from the html as if it had been rendered
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (boxRef.current && asInlineText) {
      boxRef.current.textContent = htmlToText(html, {
        separator: ". "
      })
    }
  }, [html, asInlineText])

  return (
    <Box className="HTMLText" sx={HTMLTextStyles}>
      {truncate ? (
        <TruncatedHTML html={html} {...truncateProps} />
      ) : asInlineText ? (
        <Box {...props} ref={boxRef} whiteSpace="pre-wrap" />
      ) : (
        <Box {...props} dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </Box>
  )
}

export default HTMLText
